import { CUSTOM_FILTER_SORT_ORDER } from "@/features/sub-families/resources/constants.ts";
import { formatFilterLabel } from "@/features/sub-families/utils/formatters.ts";
import { getSmartNumberDescriptorByProductProperty } from "@/features/sub-families/utils/smart-number.ts";
import Logger from "@/lib/logger.ts";
import type { Nullable, PickOptional } from "@/types";
import { getPlaceholderImage } from "@/utils/image.ts";
import { loopIterator } from "@/utils/iterator.ts";
import { compareByCustomOrder, sortItemsBy } from "@/utils/sort.ts";
import { isText, typedObjectEntries } from "@/utils/types.ts";
import {
    type Choice,
    ConfigurationTab,
    type FilterOption,
    FilterType,
    type LightAccessory,
    type MountingKit,
    type ProductData,
    type ProductPropertyKey,
    type Properties,
    type Property,
    type Supply,
} from "../types";

export const getFilterOptions = (productData: ProductData) => {
    const filterOptions = new Map<ProductPropertyKey, FilterOption>();

    for (const product of Object.values(productData)) {
        const { properties } = product;

        for (const [propertyKey, property] of typedObjectEntries(properties)) {
            if (propertyKey === "dimming_type" || propertyKey === "power_supply") continue; // These options have their own mapping

            const descriptor = getSmartNumberDescriptorByProductProperty(propertyKey);
            if (!descriptor) continue; // Make sure we always have a descriptor attached to a property. If none is present, we cannot filter!

            let choice: Nullable<Choice> = null;

            switch (propertyKey) {
                case "cct": {
                    choice = createCCTChoice(properties);
                    break;
                }

                case "cri":
                case "beam_angle":
                case "dimension": {
                    choice = propertyToChoice(property as Property);
                    break;
                }

                default: {
                    choice = null;
                }
            }

            if (!choice) continue;

            const filterOption = filterOptions.get(propertyKey);
            if (!filterOption) {
                const filterType =
                    "view" in property && property.view !== null ? FilterType.RadioImages : FilterType.Radio;

                filterOptions.set(
                    propertyKey,
                    createFilterOption({
                        label: "name" in property && isText(property.name) ? property.name : "",
                        choices: [choice],
                        descriptor,
                        filterType,
                        tab: getTabByPropertyKey(propertyKey),
                        sortKey: !Array.isArray(property) ? parseSortKey(property.sortkey) : null,
                    }),
                );

                continue;
            }

            const valueExists = filterOption.choices.some((option) => option.value === choice.value);
            if (valueExists) continue;

            filterOption.choices.push(choice);

            filterOptions.set(propertyKey, filterOption);
        }
    }

    const validFilterOptions = new Map<PropertyKey, FilterOption>();
    loopIterator(filterOptions.entries(), ([propertyKey, filterOption]) => {
        if (!isValidFilterOption(propertyKey, filterOption)) return;

        filterOption.choices = sortItemsBy(filterOption.choices, "sortKey");

        validFilterOptions.set(propertyKey, filterOption);
    });

    if (Logger.enabled) console.log("Filter options", validFilterOptions);

    const sorted = Array.from(validFilterOptions.entries()).toSorted(([a], [b]) => {
        return compareByCustomOrder(a, b, CUSTOM_FILTER_SORT_ORDER);
    });

    return Object.fromEntries(sorted) as Record<ProductPropertyKey, FilterOption>;
};

export const createFilterOption = (args: PickOptional<FilterOption, "sortKey" | "tab">): FilterOption => {
    const { label, choices, descriptor, filterType, sortKey, tab } = args;

    return {
        label,
        choices,
        descriptor,
        filterType,
        tab: tab ?? null,
        sortKey: sortKey ?? null,
    };
};

export const getValidValue = (
    property: Property,
    valueProperty: Extract<keyof Property, "value" | "originalValue">,
) => {
    const value = property[valueProperty];

    if (!value || value.length < 1) return null;

    return Array.isArray(value) ? formatFilterLabel(property) : value;
};

export const propertyToChoice = (property: Property): Nullable<Choice> => {
    const value = getValidValue(property, "value");
    const originalValue = getValidValue(property, "originalValue");

    if (!value || !originalValue) return null;

    return {
        value: originalValue,
        label: value,
        configCode: property.configCode,
        image: property.view,
        sortKey: property.sortkey,
        description: null,
        tooltip: null,
    };
};

export const createSupplyChoice = (supply: Supply): Choice => {
    return {
        label: supply.title,
        configCode: supply.code,
        value: supply.code,
        sortKey: null,
        image: null,
        description: null,
        tooltip: null,
    };
};

export const lightAccessoriesToChoices = (lightAccessories: LightAccessory[]): Choice[] => {
    return lightAccessories.map<Choice>(lightAccessoryToChoice);
};

export const mountingKitsToChoices = (mountingKits: MountingKit[]): Choice[] => {
    return mountingKits.map<Choice>(mountingKitToChoice);
};

const getTabByPropertyKey = (propertyKey: ProductPropertyKey) => {
    switch (propertyKey) {
        case "dimension":
            return ConfigurationTab.Design;

        default:
            return ConfigurationTab.Light;
    }
};

const createCCTChoice = (properties: Properties) => {
    const choice = propertyToChoice(properties.cct);

    if (choice) {
        choice.description = properties.cct_type.label;
        choice.sortKey = parseSortKey(properties.cct_type.sortkey);
    }

    return choice;
};

const isValidFilterOption = (propertyKey: ProductPropertyKey, filterOption: FilterOption) => {
    if (propertyKey === "dimension") return filterOption.choices.length > 1;

    // Specific case for when there is only 1 choice for beam_angle
    if (propertyKey === "beam_angle" && filterOption.choices.length === 1) {
        const choice = filterOption.choices[0];

        // The single choice is invalid when it is equal to 0 degrees
        if (choice.value === "00 °") return false;
    }

    return filterOption.choices.length >= 1;
};

const lightAccessoryToChoice = (lightAccessory: LightAccessory): Choice => {
    return {
        label: lightAccessory.title,
        value: lightAccessory.code,
        configCode: lightAccessory.code,
        image: lightAccessory.asset ?? getPlaceholderImage(),
        sortKey: null,
        description: null,
        tooltip: lightAccessory.description,
    };
};

const mountingKitToChoice = (mountingKit: MountingKit): Choice => {
    return {
        label: mountingKit.ceilingLabel,
        configCode: mountingKit.configCode,
        value: mountingKit.configCode,
        sortKey: null,
        image: null,
        description: null,
        tooltip: null,
    };
};

function parseSortKey(sortKey: Nullable<string>) {
    if (sortKey === null) return null;

    const asNumber = parseFloat(sortKey);
    return Number.isNaN(asNumber) ? sortKey : asNumber;
}
